import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Checkbox, Chip, CircularProgress, IconButton, InputAdornment, MenuItem, Stack,
  Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CardGiftcard as GrantIcon, PersonRemoveAlt1 as RemoveIcon, MailOutline as SendIcon,
  Search as SearchIcon, RestartAlt as ResetIcon, ReplayCircleFilled as ReopenIcon
} from '@mui/icons-material';
import OrganizationGrantModal from 'components/admin-organizations/OrganizationGrantModal';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import ConfirmActionModal from 'components/shared/modals/ConfirmActionModal';
import StyledFiltersContainer from 'common/theme/filters/StyledFiltersContainer';
import uiSlice from 'store/modules/ui';
import { createSnack } from 'common/utils/general';
import {
  ORGANIZATION_MEMBERS, REMOVE_USERS_FROM_ORGANIZATION, SEND_ORGANIZATION_ACCESS
} from 'api/graphql/organization';
import { ORGANIZATION_DEPARTMENTS, RESET_WELLBEING_CHECKIN, SET_USERS_DEPARTMENT } from 'api/graphql/wellbeing';

const PER_PAGE = 20;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const STATUS_META = {
  ACTIVE: { label: 'Activo', bg: 'rgba(27,147,75,0.16)', color: '#5FE0A0' },
  IN_ARREARS: { label: 'En mora', bg: 'rgba(249,189,78,0.16)', color: 'var(--otx-gold-text)' },
  INACTIVE: { label: 'Inactivo', bg: 'rgba(128,128,128,0.18)', color: 'text.secondary' },
  NEW: { label: 'Nuevo', bg: 'rgba(122,174,255,0.16)', color: '#7AAEFF' }
};

// "Nuevo" = sin plan asignado aún (INACTIVE pero nunca pagó); distinto del que tuvo plan y venció.
const effectiveStatus = (u) => (u.membership_status === 'INACTIVE' && !u.plan_id?.id ? 'NEW' : u.membership_status);

function OrganizationMembersTab({
  organizationId,
  onChanged = null,
  showWellbeingActions = false
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['organizations']);
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [grantOpen, setGrantOpen] = useState(false);
  const [selected, setSelected] = useState({});
  // Filtros (client-side): búsqueda por nombre/correo + estado.
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  // Confirmaciones: quitar (rojo) usa ConfirmDeleteModal; enviar acceso (dorado) usa ConfirmActionModal.
  const [removeIds, setRemoveIds] = useState(null);
  const [sendIds, setSendIds] = useState(null);
  const [reopenIds, setReopenIds] = useState(null);

  // Traemos todos los miembros de la org y filtramos/paginamos en el cliente (cantidades modestas).
  const { data, loading, refetch } = useQuery(ORGANIZATION_MEMBERS, {
    variables: { organization_id: [Number(organizationId)], first: 500, page: 1 },
    fetchPolicy: 'cache-and-network'
  });
  const [removeUsers, { loading: removing }] = useMutation(REMOVE_USERS_FROM_ORGANIZATION);
  const [sendAccess, { loading: sending }] = useMutation(SEND_ORGANIZATION_ACCESS);
  // Departamentos (áreas) — asignación masiva desde la selección.
  const { data: departmentsData } = useQuery(ORGANIZATION_DEPARTMENTS, {
    variables: { id: organizationId },
    fetchPolicy: 'cache-and-network'
  });
  const [setUsersDepartment, { loading: assigningDept }] = useMutation(SET_USERS_DEPARTMENT);
  const [resetCheckin, { loading: reopening }] = useMutation(RESET_WELLBEING_CHECKIN);
  const departments = departmentsData?.organization?.departments || [];

  const allRows = data?.users?.data ?? [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allRows.filter((u) => {
      if (statusFilter !== 'ALL' && effectiveStatus(u) !== statusFilter) return false;
      if (!term) return true;
      return u.full_name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term);
    });
  }, [allRows, search, statusFilter]);

  const total = filtered.length;
  const rows = filtered.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allChecked = rows.length > 0 && rows.every((r) => selected[r.id]);
  const filtersActive = search.trim() !== '' || statusFilter !== 'ALL';

  const toggle = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleAll = () => {
    if (allChecked) { setSelected({}); return; }
    const next = {};
    rows.forEach((r) => { next[r.id] = true; });
    setSelected(next);
  };

  const resetFilters = () => { setSearch(''); setStatusFilter('ALL'); setPage(0); };

  const snack = (variant, message) => dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `org-members-${variant}`, variant, message })));

  const statusChip = (u) => {
    const meta = STATUS_META[effectiveStatus(u)] || STATUS_META.INACTIVE;
    return <Chip size="small" label={meta.label} sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 700 }} />;
  };

  const afterChange = () => { setSelected({}); refetch(); if (onChanged) onChanged(); };

  // Asignación masiva de departamento ('' = quitar) sobre los seleccionados.
  const assignDepartment = async (departmentId) => {
    await setUsersDepartment({
      variables: { organization_id: organizationId, user_ids: selectedIds, department_id: departmentId || null }
    });
    snack('success', t('departmentAssigned', { defaultValue: 'Departamento actualizado para la selección.' }));
    afterChange();
  };

  const doRemove = async () => {
    const ids = removeIds || [];
    setRemoveIds(null);
    if (!ids.length) return;
    await removeUsers({ variables: { user_ids: ids } });
    snack('success', t('membersRemoved', { defaultValue: 'Miembros retirados de la organización.' }));
    afterChange();
  };

  // Reabrir el check-in borra las respuestas de la semana en curso: las 3 preguntas son
  // determinísticas por semana, así que sin esto no hay forma de volver a verlas hasta el lunes.
  const doReopen = async () => {
    const ids = reopenIds || [];
    setReopenIds(null);
    if (!ids.length) return;
    const res = await resetCheckin({ variables: { organization_id: organizationId, user_ids: ids } });
    const reopened = res?.data?.resetWellbeingCheckin ?? 0;
    snack('success', t('checkinReopened', { n: reopened, defaultValue: 'Check-in reabierto para {{n}} personas.' }));
    setSelected({});
  };

  const doSend = async () => {
    const ids = sendIds || [];
    setSendIds(null);
    if (!ids.length) return;
    const res = await sendAccess({ variables: { organization_id: organizationId, user_ids: ids } });
    const sent = res?.data?.sendOrganizationAccess?.sent ?? 0;
    snack('success', t('accessSentCount', { defaultValue: 'Acceso enviado a {{count}} miembros.', count: sent }));
    setSelected({});
  };

  return (
    <Box>
      {/* Filtros premium (búsqueda + estado) */}
      <StyledFiltersContainer>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            fullWidth
            placeholder={t('searchMember', { defaultValue: 'Buscar por nombre o correo' })}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
          />
          <TextField
            select
            size="small"
            label={t('status', { defaultValue: 'Estado' })}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: { md: 200 }, width: { xs: '100%', md: 'auto' } }}
          >
            <MenuItem value="ALL">{t('allStatuses', { defaultValue: 'Todos los estados' })}</MenuItem>
            <MenuItem value="ACTIVE">{t('statusActive', { defaultValue: 'Activo' })}</MenuItem>
            <MenuItem value="IN_ARREARS">{t('statusArrears', { defaultValue: 'En mora' })}</MenuItem>
            <MenuItem value="NEW">{t('statusNew', { defaultValue: 'Nuevo (sin plan)' })}</MenuItem>
            <MenuItem value="INACTIVE">{t('statusInactive', { defaultValue: 'Inactivo' })}</MenuItem>
          </TextField>
          <Button
            variant="text"
            startIcon={<ResetIcon />}
            onClick={resetFilters}
            disabled={!filtersActive}
            sx={{ textTransform: 'none', fontWeight: 700, color: 'gold.main', whiteSpace: 'nowrap' }}
          >
            {t('resetFilters', { defaultValue: 'Reiniciar filtros' })}
          </Button>
        </Stack>
      </StyledFiltersContainer>

      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
        {selectedIds.length > 0 ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            {/* Asignación masiva de departamento (Bienestar Corporativo). */}
            {departments.length > 0 && (
              <TextField
                select
                size="small"
                label={t('assignDepartment', { defaultValue: 'Asignar departamento' })}
                value=""
                disabled={assigningDept}
                onChange={(e) => assignDepartment(e.target.value)}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: { sm: 200 } }}>
                <MenuItem value="">{t('noDepartment', { defaultValue: 'Sin departamento' })}</MenuItem>
                {departments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <Button
              fullWidth={isMobile}
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={() => setSendIds(selectedIds)}
              disabled={sending}
              sx={{ textTransform: 'none', fontWeight: 700, color: 'gold.main', borderColor: 'gold.main', '&:hover': { borderColor: 'gold.main', backgroundColor: 'gold.soft' } }}
            >
              {t('sendAccess', { defaultValue: 'Enviar acceso' })} ({selectedIds.length})
            </Button>
            {showWellbeingActions && (
              <Button
                fullWidth={isMobile}
                variant="outlined"
                startIcon={<ReopenIcon />}
                onClick={() => setReopenIds(selectedIds)}
                disabled={reopening}
                sx={{ textTransform: 'none', fontWeight: 700, color: 'text.primary', borderColor: 'hairline.strong', '&:hover': { borderColor: 'text.primary' }, '&:active': { transform: 'scale(0.98)' } }}
              >
                {t('reopenCheckin', { defaultValue: 'Reabrir check-in' })} ({selectedIds.length})
              </Button>
            )}
            <Button
              fullWidth={isMobile}
              variant="text"
              startIcon={<RemoveIcon />}
              onClick={() => setRemoveIds(selectedIds)}
              disabled={removing}
              sx={{ textTransform: 'none', fontWeight: 700, color: 'gold.main' }}
            >
              {t('removeSelected', { defaultValue: 'Quitar seleccionados' })} ({selectedIds.length})
            </Button>
          </Stack>
        ) : <Box sx={{ display: { xs: 'none', sm: 'block' } }} />}
        <Button fullWidth={isMobile} variant="contained" color="primary" startIcon={<GrantIcon />} onClick={() => setGrantOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {t('grantMemberships', { defaultValue: 'Otorgar membresías' })}
        </Button>
      </Stack>

      <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.content' }}>
        {loading && allRows.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} sx={{ color: 'gold.main' }} /></Box>
        )}
        {!loading && filtered.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            {filtersActive
              ? t('noMembersMatch', { defaultValue: 'Ningún miembro coincide con los filtros.' })
              : t('noMembers', { defaultValue: 'Esta organización aún no tiene miembros.' })}
          </Box>
        )}

        {rows.length > 0 && (isMobile ? (
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1, borderBottom: (th) => `1px solid ${th.palette.hairline.main}` }}>
              <Checkbox size="small" checked={allChecked} indeterminate={selectedIds.length > 0 && !allChecked} onChange={toggleAll} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>{t('selectAll', { defaultValue: 'Seleccionar todos' })}</Typography>
            </Stack>
            {rows.map((u) => (
              <Stack key={u.id} direction="row" alignItems="center" spacing={0.5} sx={{ px: 1, py: 1.25, borderBottom: (th) => `2px solid ${th.palette.stackedRowBorder}`, '&:last-of-type': { borderBottom: 0 } }}>
                <Checkbox size="small" checked={Boolean(selected[u.id])} onChange={() => toggle(u.id)} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{u.full_name}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                    {u.email}{u.plan_id?.name ? ` · ${u.plan_id.name}` : ''}{u.department?.name ? ` · ${u.department.name}` : ''} · {fmtDate(u.organization_joined_at || u.created_at)}
                  </Typography>
                </Box>
                {statusChip(u)}
                <IconButton size="small" aria-label={t('sendAccess', { defaultValue: 'Enviar acceso' })} onClick={() => setSendIds([u.id])} sx={{ color: 'gold.main' }}><SendIcon fontSize="small" /></IconButton>
                <IconButton size="small" aria-label={t('removeFromOrg', { defaultValue: 'Quitar de la organización' })} onClick={() => setRemoveIds([u.id])} sx={{ color: (th) => th.palette.deleteAction.fg }}><RemoveIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={allChecked} indeterminate={selectedIds.length > 0 && !allChecked} onChange={toggleAll} />
                  </TableCell>
                  <TableCell>{t('name', { defaultValue: 'Nombre' })}</TableCell>
                  <TableCell>{t('email', { defaultValue: 'Correo' })}</TableCell>
                  <TableCell>{t('plan', { defaultValue: 'Plan' })}</TableCell>
                  <TableCell>{t('department', { defaultValue: 'Departamento' })}</TableCell>
                  <TableCell>{t('joinedAt', { defaultValue: 'Ingreso' })}</TableCell>
                  <TableCell align="center">{t('status', { defaultValue: 'Estado' })}</TableCell>
                  <TableCell align="right">{t('actions', { defaultValue: 'Acciones' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((u) => (
                  <TableRow key={u.id} hover selected={Boolean(selected[u.id])}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={Boolean(selected[u.id])} onChange={() => toggle(u.id)} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{u.full_name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.plan_id?.name || '—'}</TableCell>
                    <TableCell>{u.department?.name || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(u.organization_joined_at || u.created_at)}</TableCell>
                    <TableCell align="center">{statusChip(u)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('sendAccess', { defaultValue: 'Enviar acceso' })}>
                        <IconButton size="small" onClick={() => setSendIds([u.id])} sx={{ color: 'gold.main' }}><SendIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('removeFromOrg', { defaultValue: 'Quitar de la organización' })}>
                        <IconButton size="small" onClick={() => setRemoveIds([u.id])} sx={{ color: (th) => th.palette.deleteAction.fg }}><RemoveIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}

        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => { setPage(p); setSelected({}); }} rowsPerPage={PER_PAGE} rowsPerPageOptions={[PER_PAGE]} />
      </Box>

      {grantOpen && (
        <OrganizationGrantModal
          open={grantOpen}
          organizationId={organizationId}
          onClose={() => setGrantOpen(false)}
          onGranted={afterChange}
        />
      )}

      <ConfirmDeleteModal
        open={Boolean(removeIds)}
        onClose={() => setRemoveIds(null)}
        onConfirm={doRemove}
        title={t('removeMemberTitle', { defaultValue: 'Quitar de la organización' })}
        message={t('removeMemberMessage', { defaultValue: '¿Seguro que quieres quitar a {{count}} miembro(s)? Volverán a OTRIX App.', count: removeIds?.length || 0 })}
        confirmLabel={t('remove', { defaultValue: 'Quitar' })}
        note=""
      />

      <ConfirmActionModal
        open={Boolean(sendIds)}
        onClose={() => setSendIds(null)}
        onConfirm={doSend}
        loading={sending}
        title={t('sendAccessTitle', { defaultValue: 'Enviar acceso' })}
        message={t('sendAccessMessage', { defaultValue: 'Enviaremos un correo con la contraseña temporal a {{count}} miembro(s) para que definan su clave e ingresen.', count: sendIds?.length || 0 })}
        confirmLabel={t('send', { defaultValue: 'Enviar' })}
      />

      <ConfirmActionModal
        open={Boolean(reopenIds)}
        onClose={() => setReopenIds(null)}
        onConfirm={doReopen}
        loading={reopening}
        title={t('reopenCheckinTitle', { defaultValue: 'Reabrir check-in de bienestar' })}
        message={t('reopenCheckinMessage', {
          n: reopenIds?.length || 0,
          defaultValue: 'Borraremos las respuestas de esta semana de {{n}} miembro(s) para que puedan volver a responder. Los promedios de la semana se recalculan sin ellas.'
        })}
        confirmLabel={t('reopen', { defaultValue: 'Reabrir' })}
      />
    </Box>
  );
}

OrganizationMembersTab.propTypes = {
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChanged: PropTypes.func,
  showWellbeingActions: PropTypes.bool
};

export default OrganizationMembersTab;
